import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import { subscriptionService } from '../../services/subscription';
import faqService from '../../services/faq';
import type { SubscriptionPlan } from '../../types/subscription';
import type { FaqQuestion } from '../../types/faq';
import { useLanguageLink } from '../../hooks/useLanguageLink';

export default function PricingPage() {
  const { t, i18n } = useTranslation('pricing');
  const { createLink } = useLanguageLink();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [faqs, setFaqs] = useState<FaqQuestion[]>([]);
  const [faqsLoading, setFaqsLoading] = useState(true);

  // Helper function to get translated FAQ text
  const getTranslatedText = (defaultText: string, translations?: { [key: string]: string }): string => {
    if (!translations) return defaultText;

    const currentLang = i18n.language;
    return translations[currentLang] || defaultText;
  };

  // Fetch subscription plans and FAQs on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await subscriptionService.getAvailablePlans();
        // Filter out Free Trial, only show paid plans
        const paidPlans = response.plans.filter(plan => plan.slug !== 'free-trial');
        setSubscriptionPlans(paidPlans);
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error);
      } finally {
        setPlansLoading(false);
      }
    };

    const fetchFaqs = async () => {
      try {
        const response = await faqService.getPublicFaqs('pricing-plans');
        // Get questions from the first (and only) category
        const questions = response.categories[0]?.questions || [];
        setFaqs(questions);
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
      } finally {
        setFaqsLoading(false);
      }
    };

    fetchPlans();
    fetchFaqs();
  }, []);

  // Build features array from subscription plan data
  const buildFeatures = (plan: SubscriptionPlan): string[] => {
    const features: string[] = [];

    // Campaigns
    if (plan.limits.campaigns === -1) {
      features.push(t('plans.unlimited_campaigns'));
    } else {
      features.push(t('plans.active_campaigns', { count: plan.limits.campaigns }));
    }

    // Requests
    if (plan.limits.requests_per_month === -1) {
      features.push(t('plans.unlimited_requests'));
    } else {
      const requestsK = (plan.limits.requests_per_month / 1000).toFixed(0);
      features.push(t('plans.requests_per_month', { count: requestsK }));
    }

    // Team members
    if (plan.limits.team_members === -1) {
      features.push(t('plans.unlimited_team_members'));
    } else {
      const key = plan.limits.team_members > 1 ? 'plans.team_members' : 'plans.team_member';
      features.push(t(key, { count: plan.limits.team_members }));
    }

    // Common features for all plans
    features.push(t('plans.ipv6_support'));

    // Add tier-specific features based on plan slug
    if (plan.slug === 'starter') {
      features.push(t('plans.basic_analytics'));
      features.push(t('plans.email_support'));
      features.push(t('plans.data_retention_days', { count: plan.limits.data_retention_days || 7 }));
    } else if (plan.slug === 'pro') {
      features.push(t('plans.advanced_analytics'));
      features.push(t('plans.priority_support'));
      features.push(t('plans.data_retention_days', { count: plan.limits.data_retention_days || 30 }));
      features.push(t('plans.custom_filters'));
      features.push(t('plans.api_access'));
    } else if (plan.slug === 'enterprise') {
      features.push(t('plans.custom_analytics'));
      features.push(t('plans.dedicated_support'));
      features.push(t('plans.data_retention_days', { count: plan.limits.data_retention_days || 90 }));
      features.push(t('plans.custom_integration'));
      features.push(t('plans.sla_guarantee'));
    }

    // Add custom features from database
    if (plan.features && plan.features.length > 0) {
      features.push(...plan.features);
    }

    return features;
  };

  // FAQ schema data for SEO (using fetched FAQs with translations)
  const faqSchemaData = faqs.map(faq => ({
    question: getTranslatedText(faq.question, faq.question_translations),
    answer: getTranslatedText(faq.answer, faq.answer_translations),
  }));


  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title={i18n.language === 'ru'
          ? 'Тарифы — Защита рекламы от ботов и скликивания'
          : 'Pricing - Traffic Filtering Plans'}
        description={i18n.language === 'ru'
          ? 'Простые тарифы для защиты рекламы. От $29/месяц. Бесплатный период 14 дней, без привязки карты. Защитите бюджет от ботов и скликивания.'
          : 'Simple, transparent pricing for Axetra traffic filtering. Plans start at $29/month. 14-day free trial, no credit card required. Protect your ad campaigns from bots and click fraud.'}
        canonical={`/${i18n.language}/pricing`}
        lang={i18n.language}
        hreflang={[
          { lang: 'en', href: 'https://www.axetra.io/en/pricing' },
          { lang: 'ru', href: 'https://www.axetra.io/ru/pricing' },
          { lang: 'de', href: 'https://www.axetra.io/de/pricing' },
        ]}
        keywords={i18n.language === 'ru'
          ? 'тарифы антифрод, цены защита от скликивания, стоимость сервиса защиты рекламы'
          : 'traffic filtering pricing, bot detection cost, click fraud protection pricing, ad protection pricing'}
      />

      {/* Schema Markup */}
      <SchemaMarkup
        type="product"
        name="Axetra Professional Plan"
        price="79"
        description="Advanced traffic filtering with ML-powered bot detection for growing businesses"
      />
      {faqSchemaData.length > 0 && (
        <SchemaMarkup
          type="faq"
          questions={faqSchemaData}
        />
      )}

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-primary-100 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-30" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30" />
        <div className="container-marketing relative">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                {t('hero.title')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('hero.subtitle')}
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{t('hero.free_trial')}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{t('hero.no_credit_card')}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{t('hero.cancel_anytime')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="container-marketing">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {plansLoading ? (
              // Loading skeleton
              [...Array(3)].map((_, index) => (
                <div key={index} className="pricing-card animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4 w-3/4 mx-auto"></div>
                  <div className="h-12 bg-gray-200 rounded mb-6"></div>
                  <div className="space-y-3 mb-8">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              subscriptionPlans.map((plan, index) => {
                // Mark the middle plan (Pro) as popular
                const isPopular = index === 1 || plan.slug === 'pro';
                const isEnterprise = plan.slug === 'enterprise';

                // Format price
                const priceDisplay = plan.price === 0 ? t('plans.free') : `$${Math.round(plan.price)}`;

                // Get description - prioritize translations, fall back to database description
                const translatedDescription = t(`plans.descriptions.${plan.slug}`, { defaultValue: '' });
                const description = translatedDescription || plan.description || '';
                const features = buildFeatures(plan);
                const cta = t('plans.get_started');
                const ctaLink = createLink('register');

                return (
                  <div
                    key={plan.id}
                    className={`pricing-card ${isPopular ? 'pricing-card-popular' : ''}`}
                  >
                    {isPopular && (
                      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-0">
                        <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-gradient-to-r from-primary-600 to-purple-600 text-white shadow-lg">
                          {t('plans.most_popular')}
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      <p className="text-gray-600 text-sm mb-4">{description}</p>
                      <div className="flex items-end justify-center gap-1">
                        <span className="text-5xl font-bold text-gray-900">{priceDisplay}</span>
                        {!isEnterprise && <span className="text-gray-600 mb-2">{t('plans.per_month')}</span>}
                      </div>
                    </div>

                    <ul className="space-y-4 mb-8">
                      {features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={ctaLink}
                      className={`block w-full text-center px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                        isPopular
                          ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white hover:shadow-xl hover:-translate-y-0.5'
                          : 'bg-white text-primary-600 border-2 border-primary-200 hover:border-primary-400 hover:bg-primary-50'
                      }`}
                    >
                      {cta}
                    </Link>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-marketing max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('faq.title')}</h2>
            <p className="text-lg text-gray-600">{t('faq.subtitle')}</p>
          </div>

          <div className="space-y-4">
            {faqsLoading ? (
              // Loading skeleton
              [...Array(4)].map((_, index) => (
                <div key={index} className="bg-white rounded-xl border-2 border-gray-200 p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))
            ) : faqs.length === 0 ? (
              <p className="text-center text-gray-500">No FAQs available.</p>
            ) : (
              faqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-primary-300 transition-all duration-200"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900">
                      {getTranslatedText(faq.question, faq.question_translations)}
                    </span>
                    <svg
                      className={`w-6 h-6 text-primary-600 flex-shrink-0 transition-transform duration-200 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 text-gray-700 leading-relaxed animate-fade-in">
                      {getTranslatedText(faq.answer, faq.answer_translations)}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">{t('faq.more_questions')}</p>
            <Link
              to={createLink('contact')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 border-2 border-primary-200 rounded-lg font-semibold hover:border-primary-400 hover:bg-primary-50 transition-all duration-200"
            >
              {t('faq.contact_support')}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container-marketing text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            {t('cta.subtitle')}
          </p>
          <Link
            to={createLink('register')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:shadow-2xl hover:-translate-y-1 transition-all duration-200"
          >
            {t('cta.button')}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
          <p className="mt-4 text-purple-200 text-sm">
            {t('hero.no_credit_card')} • {t('hero.free_trial')} • {t('hero.cancel_anytime')}
          </p>
        </div>
      </section>
    </div>
  );
}
